import { Injectable } from '@angular/core';
import { Proxied } from '@creately/sakota';
import { Random } from 'flux-core';
import { LogicClassFactory } from 'flux-diagram-composer';
import { DefinitionLocator } from '../../../base/shape/definition/definition-locator.svc';
import { ShapeModel } from '../../../base/shape/model/shape.mdl';
import { DiagramModel } from '../../../base/diagram/model/diagram.mdl';
import { fromPromise } from 'rxjs/internal-compatibility';

/**
 * ConnectorBarService
 * Service responsible for adding connector bars to the change model
 */
@Injectable()
export class ConnectorBarService {
    constructor(
        protected defLocator: DefinitionLocator,
    ) {}

    /**
     * Adds a connector bar between two shapes if one doesn't already exist
     * @param shapeAId The ID of the first shape
     * @param shapeBId The ID of the second shape
     * @param changeModel The diagram change model
     * @returns Promise or null if connector bar already exists or shouldn't be added
     */
    public addConnectorBar(shapeAId: string, shapeBId: string, changeModel: Proxied<DiagramModel>): any {
        if (!shapeAId || !shapeBId) {
            return null;
        }

        const newShapeId = Random.shapeId();

        // Prevent adding multiple connector bars between the same 2 shapes
        if ((changeModel.shapes[shapeAId] as ShapeModel).connectorBarId && 
            (changeModel.shapes[shapeBId] as ShapeModel).connectorBarId &&
            changeModel.shapes[(changeModel.shapes[shapeBId] as ShapeModel).connectorBarId] &&
            changeModel.shapes[(changeModel.shapes[shapeAId] as ShapeModel).connectorBarId]
        ) {
            return null; // Connector bar already exists
        }

        const shape = {
            id: newShapeId,
            defId: 'creately.basic.connectorbar',
            version: 1,
            zIndex: 0,
            x: 0,
            y: 0,
            connectorBarData: {
                shapeAId,
                shapeBId,
            }
        } as any;

        (changeModel.shapes[shapeAId] as any).connectorBarId = shape.id;
        (changeModel.shapes[shapeBId] as any).connectorBarId = shape.id;
        shape.connectorBarShapeAId = shapeAId;
        shape.connectorBarShapeBId = shapeBId;

        changeModel.shapes[newShapeId] = shape;

        return fromPromise(this.createConnectorBar(shape, changeModel));
    }

    /**
     * Creates and initializes a connector bar using its logic class
     * @param shape The connector bar shape to create
     * @param changeModel The diagram change model
     * @returns Promise that resolves when the connector bar is created
     */
    protected async createConnectorBar(shape: any, changeModel: Proxied<DiagramModel>): Promise<void> {
        const def: any = await this.defLocator.getDefinition(shape.defId, shape.version).toPromise();
        const logicClass = await this.defLocator.getClass(def.logicClass).toPromise();
        const instance = LogicClassFactory.instance.create(logicClass);
        (instance as any).created(changeModel.shapes[shape.id], def, changeModel);
    }
}